import { createContext, useContext, useEffect, useState } from "react";

// Dashboard redesign — dark mode. react-bootstrap reads data-bs-theme on <html>
// natively; theme.css flips every --bs-*/--mca-* token on that attribute.
// The inline script in public/index.html sets the attribute before first paint
// (no flash); this provider keeps it in sync + exposes a toggle.
export const ThemeContext = createContext();

const STORAGE_KEY = "mca-theme"; // 'light' | 'dark' | 'auto'
const VALID = ["light", "dark", "auto"];

const resolve = (pref) =>
  pref === "auto"
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light"
    : pref;

const apply = (pref) =>
  document.documentElement.setAttribute("data-bs-theme", resolve(pref));

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && VALID.includes(stored) ? stored : "auto";
  });

  useEffect(() => {
    apply(theme);
  }, [theme]);

  // Follow the OS when in 'auto'.
  useEffect(() => {
    if (theme !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => apply("auto");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = (next) => {
    if (!VALID.includes(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setThemeState(next);
  };

  const toggleTheme = () => setTheme(resolve(theme) === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, resolved: resolve(theme), setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
};
